import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Plus } from 'lucide-react';
import { AuthShell } from './Auth';
import { useApp } from '../context/AppContext';
import { interestOptions, AVATAR_CHOICES } from '../lib/constants';
import { toast } from 'sonner';

export default function Onboarding() {
  const navigate = useNavigate();
  const { profiles, addProfile, setActiveProfileId, interests, setInterests, user } = useApp();
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(profiles[0]?.id || null);
  React.useEffect(() => { if (!selected && profiles[0]) setSelected(profiles[0].id); }, [profiles, selected]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState(user?.name?.split(' ')[0] || '');
  const [avatar, setAvatar] = useState(AVATAR_CHOICES[2]);
  const [picked, setPicked] = useState(interests);

  const createProfile = async () => {
    if (!name.trim()) { toast.error('Give your profile a name'); return; }
    const p = await addProfile({ name: name.trim(), avatar, language: 'English', maturity: '18+', kids: false, autoplay: true, subtitles: 'Off' });
    if (p) { setSelected(p.id); setCreating(false); toast.success(`Profile "${p.name}" created`); }
  };
  const finish = () => { setActiveProfileId(selected); setInterests(picked); toast.success('Welcome to Clax'); navigate('/'); };

  return (
    <AuthShell wide>
      <div className="flex items-center gap-2 mb-6" data-testid="onboarding-steps">
        {['Profile', 'Interests', 'Enter Clax'].map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 text-[13px] ${step >= i + 1 ? 'text-[#FFFFFF]' : 'text-[#8A8694]'}`}><span className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center ${step > i + 1 ? 'bg-[#4ADE80] text-black' : step === i + 1 ? 'brand-grad' : 'bg-white/[0.06]'}`}>{step > i + 1 ? <Check size={12} /> : i + 1}</span>{s}</div>
            {i < 2 && <span className="flex-1 h-px bg-white/[0.06]" />}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <div data-testid="onboarding-profile">
          <h1 className="text-[24px] font-bold">Who's watching?</h1>
          <p className="text-[13.5px] text-[#C7C4CB] mt-1">Select a profile or create a new one.</p>
          <div className="flex flex-wrap gap-5 mt-6">
            {profiles.map((p) => (
              <button key={p.id} onClick={() => setSelected(p.id)} className="group text-center" data-testid={`pick-profile-${p.id}`}>
                <div className={`w-[96px] h-[96px] rounded-xl overflow-hidden ring-2 transition ${selected === p.id ? 'ring-[#8B2CFF]' : 'ring-transparent group-hover:ring-white/40'}`}><img src={p.avatar} alt="" className="w-full h-full object-cover" /></div>
                <div className={`mt-2 text-[13px] ${selected === p.id ? 'text-[#FFFFFF]' : 'text-[#C7C4CB]'}`}>{p.name}</div>
              </button>
            ))}
            <button onClick={() => setCreating(true)} className="text-center group" data-testid="add-profile"><div className="w-[96px] h-[96px] rounded-xl bg-white/[0.04] border border-dashed border-[#DDD6EC] flex items-center justify-center group-hover:bg-white/[0.08] transition"><Plus className="text-[#C7C4CB]" /></div><div className="mt-2 text-[13px] text-[#C7C4CB]">Add profile</div></button>
          </div>
          {creating && (
            <div className="elevated p-4 mt-6 space-y-3">
              <input className="clax-input" placeholder="Profile name" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-profile-name" />
              <div className="flex gap-2 flex-wrap">{AVATAR_CHOICES.map((a) => <button key={a} onClick={() => setAvatar(a)} className={`w-12 h-12 rounded-lg overflow-hidden ring-2 ${avatar === a ? 'ring-[#8B2CFF]' : 'ring-transparent'}`}><img src={a} alt="" className="w-full h-full object-cover" /></button>)}</div>
              <div className="flex gap-2"><button onClick={createProfile} className="btn-brand !h-10" data-testid="create-profile">Create</button><button onClick={() => setCreating(false)} className="btn-ghost !h-10">Cancel</button></div>
            </div>
          )}
          <div className="flex justify-end mt-8"><button disabled={!selected} onClick={() => setStep(2)} className="btn-brand" data-testid="onboarding-next-1">Continue <ArrowRight size={16} /></button></div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="onboarding-interests">
          <h1 className="text-[24px] font-bold">What do you love?</h1>
          <p className="text-[13.5px] text-[#C7C4CB] mt-1">Pick at least 3 to personalise your home.</p>
          <div className="flex flex-wrap gap-2 mt-6">{interestOptions.map((o) => <button key={o} onClick={() => setPicked((p) => (p.includes(o) ? p.filter((x) => x !== o) : [...p, o]))} className={`chip !h-10 !px-5 !text-[14px] ${picked.includes(o) ? 'on' : ''}`} data-testid={`interest-${o.toLowerCase().replace(/\s+/g, '-')}`}>{picked.includes(o) && <Check size={14} className="mr-1.5" />}{o}</button>)}</div>
          <div className="flex justify-between mt-8"><button onClick={() => setStep(1)} className="btn-ghost">Back</button><button disabled={picked.length < 3} onClick={() => setStep(3)} className="btn-brand" data-testid="onboarding-next-2">Continue <ArrowRight size={16} /></button></div>
        </div>
      )}

      {step === 3 && (
        <div className="text-center py-6" data-testid="onboarding-done">
          <img src={profiles.find((p) => p.id === selected)?.avatar} alt="" className="w-24 h-24 rounded-2xl object-cover mx-auto ring-2 ring-[#8B2CFF]" />
          <h1 className="text-[26px] font-bold mt-5">You're all set, {profiles.find((p) => p.id === selected)?.name}.</h1>
          <p className="text-[14px] text-[#C7C4CB] mt-2">We've tuned Clax around {picked.slice(0, 3).join(', ')}{picked.length > 3 ? ` and ${picked.length - 3} more` : ''}.</p>
          <button onClick={finish} className="btn-brand mt-8 !h-12 !px-8" data-testid="enter-clax">Enter Clax <ArrowRight size={16} /></button>
        </div>
      )}
    </AuthShell>
  );
}
